/**
 * ProductVideoUpload - Video upload component for product management
 * Feature flagged via REACT_APP_PRODUCT_VIDEO_ENABLED
 */
import { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import api from "../lib/api";
import {
  Video,
  Upload,
  Trash2,
  Play,
  Pause,
  X,
  FileVideo,
  Clock,
  HardDrive,
  AlertCircle
} from "lucide-react";

const MAX_SIZE_MB = 20;
const MAX_DURATION_SEC = 30;

export default function ProductVideoUpload({ productId, videos = [], onVideoChange }) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [playing, setPlaying] = useState(false);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);

  // Check if feature is enabled
  const videoEnabled = process.env.REACT_APP_PRODUCT_VIDEO_ENABLED === "true";

  if (!videoEnabled) {
    return null;
  }

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (file.type !== "video/mp4") {
      toast.error("Only MP4 videos are allowed");
      return;
    }

    // Validate file size
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > MAX_SIZE_MB) {
      toast.error(`Video too large (${sizeMB.toFixed(1)}MB). Maximum is ${MAX_SIZE_MB}MB`);
      return;
    }

    // Check duration client-side (optional pre-check)
    const duration = await getVideoDuration(file);
    if (duration && duration > MAX_DURATION_SEC) {
      toast.error(`Video too long (${Math.round(duration)}s). Maximum is ${MAX_DURATION_SEC} seconds`);
      return;
    }

    // Upload video
    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await api.post(
        `/products/${productId}/videos`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        }
      );

      toast.success("Video uploaded successfully!");
      
      if (onVideoChange) {
        onVideoChange([...(videos || []), response.data.video]);
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to upload video";
      toast.error(message);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const getVideoDuration = (file) => {
    return new Promise((resolve) => {
      const video = document.createElement("video");
      video.preload = "metadata";
      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        resolve(video.duration);
      };
      video.onerror = () => resolve(null);
      video.src = URL.createObjectURL(file);
    });
  };

  const handleDelete = async (videoId) => {
    if (!confirm("Delete this video?")) return;

    try {
      await api.delete(`/products/${productId}/videos/${videoId}`);
      toast.success("Video deleted");
      
      if (onVideoChange) {
        onVideoChange(videos.filter(v => v.id !== videoId));
      }
    } catch (error) {
      toast.error("Failed to delete video");
    }
  };

  const formatBytes = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return mins > 0 ? `${mins}:${secs.toString().padStart(2, "0")}` : `${secs}s`;
  };

  const currentVideo = videos?.[0];
  const hasVideo = !!currentVideo;

  return (
    <Card className="border border-gray-200">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Video className="h-4 w-4 text-emerald-500" />
          Product Video
          <Badge variant="outline" className="text-xs">Optional</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Upload Area */}
        {!hasVideo && !uploading && (
          <div
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/50 transition-colors"
          >
            <FileVideo className="h-10 w-10 mx-auto mb-3 text-gray-400" />
            <p className="text-sm font-medium text-gray-700">Upload Product Video</p>
            <p className="text-xs text-gray-500 mt-1">MP4 only, max {MAX_SIZE_MB}MB, max {MAX_DURATION_SEC}s</p>
            <Button variant="outline" size="sm" className="mt-3">
              <Upload className="h-4 w-4 mr-2" />
              Select Video
            </Button>
          </div>
        )}

        {/* Upload Progress */}
        {uploading && (
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="animate-pulse">
                <Video className="h-5 w-5 text-emerald-500" />
              </div>
              <span className="text-sm font-medium">Uploading video...</span>
              <span className="text-sm text-gray-500 ml-auto">{uploadProgress}%</span>
            </div>
            <Progress value={uploadProgress} className="h-2" />
            <p className="text-xs text-gray-500 mt-2">
              Validating duration and processing...
            </p>
          </div>
        )}

        {/* Video Preview */}
        {hasVideo && !uploading && (
          <div className="space-y-3">
            <div className="relative rounded-lg overflow-hidden bg-black aspect-video">
              <video
                ref={videoRef}
                src={`${process.env.REACT_APP_BACKEND_URL}${currentVideo.url}`}
                controls
                playsInline
                preload="metadata"
                className="w-full h-full object-contain"
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
              />
            </div>

            {/* Video Info */}
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-4 text-gray-500">
                <span className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" />
                  {formatDuration(currentVideo.duration_sec)}
                </span>
                <span className="flex items-center gap-1">
                  <HardDrive className="h-3.5 w-3.5" />
                  {formatBytes(currentVideo.size_bytes)}
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                onClick={() => handleDelete(currentVideo.id)}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Remove
              </Button>
            </div>
          </div>
        )}

        {/* Guidelines */}
        <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg text-xs text-amber-800">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">Video Guidelines</p>
            <ul className="mt-1 space-y-0.5 text-amber-700">
              <li>• Maximum duration: {MAX_DURATION_SEC} seconds</li>
              <li>• Maximum file size: {MAX_SIZE_MB}MB</li>
              <li>• Format: MP4 only</li>
              <li>• Tip: Show product in use or 360° view</li>
            </ul>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="video/mp4"
          onChange={handleFileSelect}
          className="hidden"
        />
      </CardContent>
    </Card>
  );
}
